import {
  Page, Layout, Card, Text, BlockStack, InlineGrid, Box, Button, Banner,
  InlineStack, Badge, Divider,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { recomputeMatches, type RecomputeResult } from '../api/matches';
import { syncShopifyFitment, type SyncResponse } from '../api/shopify-sync';
import { getWheelStats } from '../api/wheels';
import { getVehicleFilterOptions } from '../api/vehicles';
import { listImports, triggerVendorPull, uploadWheelProsInventory, type VendorPullOption } from '../api/imports';
import { getVendorConnections, getFeedStatus } from '../api/feeds';
import { ago } from './WheelsList';

// Dashboard-style home. Operators land here first — what they need in one
// glance: how much data is loaded, how fresh it is, and the most-used
// actions (pull vendors, recompute, sync Shopify). Everything else is one
// click away in the sidebar.
export function Home() {
  const navigate = useNavigate();

  const wheelStats = useQuery({ queryKey: ['wheels-stats'], queryFn: getWheelStats, staleTime: 60_000 });
  const vehicleOpts = useQuery({ queryKey: ['vehicle-filter-options', null], queryFn: () => getVehicleFilterOptions(), staleTime: 60_000 });
  const imports = useQuery({ queryKey: ['imports'], queryFn: listImports, staleTime: 30_000, refetchInterval: 15_000 });
  const conns = useQuery({ queryKey: ['vendor-connections'], queryFn: getVendorConnections, refetchInterval: 30_000 });
  const feeds = useQuery({ queryKey: ['feed-status'], queryFn: getFeedStatus, refetchInterval: 30_000 });

  const recomputeAll = useMutation<RecomputeResult, Error, void>({
    mutationFn: () => recomputeMatches({ scope: 'all' }),
  });
  const syncAll = useMutation<SyncResponse, Error, void>({
    mutationFn: () => syncShopifyFitment('all', { force: false }),
  });
  const pullVendor = useMutation<{ id: number; vendor: VendorPullOption }, Error, VendorPullOption>({
    mutationFn: (vendor) => triggerVendorPull(vendor),
  });

  // WheelPros inventory-by-location CSV upload. One-shot — no job row, just
  // an in-place update of qty buckets + install_speed on every matched SKU.
  // Result shown in a toast so the admin sees "X wheels moved to same-day".
  const wpInventory = useMutation({
    mutationFn: (file: File) => uploadWheelProsInventory(file),
  });

  const latestImport = imports.data?.data?.[0];
  const latestImportFresh = latestImport?.applied_at ?? latestImport?.created_at ?? null;
  const totalWheels = wheelStats.data?.total ?? 0;
  const totalVehicles = vehicleOpts.data?.makes?.reduce((s, m) => s + m.count, 0) ?? 0;
  const wpInventoryState = conns.data?.wheelpros?.feeds?.inventory?.connection_state ?? 'missing';
  const wpInventoryHelp = conns.data?.wheelpros?.feeds?.inventory?.help ?? null;
  const showWPInventoryBanner = wpInventoryState === 'missing' || wpInventoryState === 'stale';

  return (
    <Page title="Road Runner Fitment" subtitle="Vehicle & wheel fitment management">
      <Layout>
        {/* ——— top-line KPIs ——— */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
            <StatCard
              label="Vehicles"
              value={totalVehicles.toLocaleString()}
              hint={`${(vehicleOpts.data?.makes?.length ?? 0)} makes`}
              onClick={() => navigate('/vehicles')}
            />
            <StatCard
              label="Wheels"
              value={totalWheels.toLocaleString()}
              hint={wheelStats.data?.by_source?.map((s) => `${s.source_guide}: ${s.count.toLocaleString()}`).join(' · ')}
              onClick={() => navigate('/wheels')}
            />
            <StatCard
              label="Bolt patterns indexed"
              value={(vehicleOpts.data?.bolt_patterns?.length ?? 0).toLocaleString()}
              hint="distinct canonical keys across vehicles"
              onClick={() => navigate('/bolt-patterns')}
            />
            <StatCard
              label="Last import"
              value={latestImportFresh ? ago(latestImportFresh) : '—'}
              hint={latestImport ? `${latestImport.source_guide} · ${latestImport.status}` : 'no imports yet'}
              onClick={() => navigate('/imports')}
            />
          </InlineGrid>
        </Layout.Section>

        {/* ——— vendor feeds status (read-only) ——— */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">Vendor feeds</Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Latest pull status per vendor. Click <a onClick={() => navigate('/vendor-connections')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Vendor connections</a> for full history.
                  </Text>
                </BlockStack>
              </InlineStack>
              {showWPInventoryBanner && (
                <Banner
                  tone={wpInventoryState === 'missing' ? 'warning' : 'info'}
                  title={wpInventoryState === 'missing'
                    ? 'Wheel Pros inventory feed not connected'
                    : 'Wheel Pros inventory feed is stale'}
                  action={{
                    content: 'Upload Inventory CSV',
                    onAction: () => {
                      const el = document.getElementById('wp-inventory-upload');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    },
                  }}
                >
                  <p>
                    {wpInventoryHelp ??
                      "Wheel Pros' SFTP catalog does not include live qty by warehouse. Upload the Inventory by Location CSV to enable real-time stock + same-day install timing on the storefront."}
                  </p>
                </Banner>
              )}
              <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
                {/* Wheel Pros: Catalog + Inventory split */}
                <Card background="bg-surface-secondary">
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">Wheel Pros</Text>
                    <FeedStatusLine
                      label="Catalog (TechGuide)"
                      state={conns.data?.wheelpros?.feeds?.catalog?.connection_state ?? 'unknown'}
                      lastPullAt={conns.data?.wheelpros?.feeds?.catalog?.last_pull_at ?? null}
                      rowsProcessed={feeds.data?.wheelpros?.feeds?.catalog?.rows_processed ?? null}
                      newCount={feeds.data?.wheelpros?.feeds?.catalog?.new_count ?? null}
                      updateCount={feeds.data?.wheelpros?.feeds?.catalog?.update_count ?? null}
                    />
                    <FeedStatusLine
                      label="Inventory (CSV upload)"
                      state={conns.data?.wheelpros?.feeds?.inventory?.connection_state ?? 'missing'}
                      lastPullAt={conns.data?.wheelpros?.feeds?.inventory?.last_pull_at ?? null}
                      rowsProcessed={conns.data?.wheelpros?.feeds?.inventory?.rows_processed ?? null}
                    />
                  </BlockStack>
                </Card>

                {/* ATD: combined */}
                <Card background="bg-surface-secondary">
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">ATD</Text>
                    <FeedStatusLine
                      label="Catalog + Inventory (SOAP)"
                      state={conns.data?.atd?.feeds?.combined?.connection_state ?? conns.data?.atd?.connection_state ?? 'unknown'}
                      lastPullAt={conns.data?.atd?.feeds?.combined?.last_pull_at ?? conns.data?.atd?.last_pull_at ?? null}
                      rowsProcessed={feeds.data?.atd?.rows_processed ?? null}
                      newCount={feeds.data?.atd?.new_count ?? null}
                      updateCount={feeds.data?.atd?.update_count ?? null}
                    />
                  </BlockStack>
                </Card>
              </InlineGrid>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* ——— vendor pulls ——— */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">Pull fresh vendor data</Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Each button pulls that vendor's feed directly. Runs in the background — watch progress in <a onClick={() => navigate('/imports')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Imports</a>.
                  </Text>
                </BlockStack>
              </InlineStack>
              <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
                <VendorCard
                  vendor="ATD"
                  loading={pullVendor.isPending && pullVendor.variables === 'ATD'}
                  onClick={() => {
                    if (!confirm('Pull fresh ATD wheel data? Takes ~5-10 min. Runs in background.')) return;
                    pullVendor.mutate('ATD', { onSuccess: (r) => navigate(`/imports/${r.id}`) });
                  }}
                  count={wheelStats.data?.by_source?.find((s) => s.source_guide === 'ATD')?.count ?? 0}
                  source="ATD SOAP · BoltPattern2 preserved"
                />
                <VendorCard
                  vendor="Wheel Pros"
                  loading={pullVendor.isPending && pullVendor.variables === 'WheelPros'}
                  onClick={() => {
                    if (!confirm('Pull fresh Wheel Pros catalog + MAP pricing? Takes ~1 min. Runs in background.')) return;
                    pullVendor.mutate('WheelPros', { onSuccess: (r) => navigate(`/imports/${r.id}`) });
                  }}
                  count={wheelStats.data?.by_source?.find((s) => s.source_guide === 'WheelPros')?.count ?? 0}
                  source="SFTP · includes MAP feed"
                />
              </InlineGrid>
              {pullVendor.error && (
                <Banner tone="critical" title="Vendor pull failed to queue">{pullVendor.error.message}</Banner>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* ——— MAP compliance summary ——— */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">MAP compliance</Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Catalog-side MAP audit, vendor coverage stats, recent MAP changes,
                    and on-demand Shopify storefront scan.
                  </Text>
                </BlockStack>
                <Button onClick={() => navigate('/map-compliance')}>Open MAP compliance</Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* ——— WheelPros inventory-by-location upload ——— */}
        <Layout.Section>
          <div id="wp-inventory-upload" />
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">Wheel Pros inventory upload</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Export the per-SKU-by-location CSV from your Wheel Pros dealer portal and upload it
                here. The system maps column 1014 → same-day install, column 1088 → next-day,
                everything else → 3-5 days, then updates every matching Wheel Pros wheel.
              </Text>
              <InlineStack gap="300" blockAlign="center" wrap>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  style={{ fontSize: 14 }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    wpInventory.mutate(f, {
                      onSuccess: (r) => {
                        alert(
                          `WP inventory applied:\n` +
                          `  read:     ${r.stats.rows_read.toLocaleString()}\n` +
                          `  matched:  ${r.stats.skus_matched.toLocaleString()}\n` +
                          `  not found: ${r.stats.skus_not_found.toLocaleString()}\n` +
                          `  same-day:  ${r.stats.same_day_count.toLocaleString()}\n` +
                          `  next-day:  ${r.stats.next_day_count.toLocaleString()}\n` +
                          `  standard:  ${r.stats.standard_count.toLocaleString()}\n` +
                          `  OOS:       ${r.stats.out_of_stock_count.toLocaleString()}`,
                        );
                      },
                      onError: (err) => alert(`Upload failed: ${(err as Error).message}`),
                    });
                  }}
                  disabled={wpInventory.isPending}
                />
                {wpInventory.isPending && <Text as="span" tone="subdued">uploading & applying…</Text>}
              </InlineStack>
              {wpInventory.error && (
                <Banner tone="critical" title="WP inventory upload failed">{(wpInventory.error as Error).message}</Banner>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* ——— quick nav ——— */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 3 }} gap="400">
            <NavTile
              title="Vehicles"
              desc="Year → Make → Model rows · fitment envelope · bolt patterns"
              onClick={() => navigate('/vehicles')}
            />
            <NavTile
              title="Wheels"
              desc="Catalog · specs · pricing · freshness · image status"
              onClick={() => navigate('/wheels')}
            />
            <NavTile
              title="Imports"
              desc="Upload RedBook / vendor CSVs · review job history"
              onClick={() => navigate('/imports')}
            />
          </InlineGrid>
        </Layout.Section>

        {/* ——— maintenance actions ——— */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">Maintenance</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Manual triggers. Both run automatically after each successful import — use these when a scheduled sync is overdue or after bulk edits.
                </Text>
              </BlockStack>

              <ActionRow
                label="Recompute fitment matches"
                detail="Rebuilds fitment_matches from vehicles × wheel_products."
                buttonLabel={recomputeAll.isPending ? 'Recomputing…' : 'Recompute matches'}
                loading={recomputeAll.isPending}
                variant="primary"
                onClick={() => {
                  if (!confirm('Recompute fitment matches for every active vehicle & wheel? Takes a few minutes.')) return;
                  recomputeAll.mutate();
                }}
              />
              {recomputeAll.data && (
                <Banner tone="success">
                  Done — inserted {recomputeAll.data.inserted ?? 0}, deleted {recomputeAll.data.deleted ?? 0}, kept {recomputeAll.data.kept ?? 0}
                  {recomputeAll.data.totalVehicles != null && ` · ${recomputeAll.data.totalVehicles} vehicles × ${recomputeAll.data.totalWheels} wheels`}
                </Banner>
              )}
              {recomputeAll.error && <Banner tone="critical" title="Recompute failed">{recomputeAll.error.message}</Banner>}

              <Divider />

              <ActionRow
                label="Re-sync Shopify fitment metafields"
                detail="Writes fitment.vehicles, fitment.summary, fitment.synced_at per wheel."
                buttonLabel={syncAll.isPending ? 'Syncing…' : 'Re-sync Shopify'}
                loading={syncAll.isPending}
                onClick={() => {
                  if (!confirm('Push fitment metafields to Shopify for every wheel? Unchanged wheels are skipped.')) return;
                  syncAll.mutate();
                }}
              />
              {syncAll.data && (
                <Banner tone={syncAll.data.summary.errors > 0 ? 'warning' : 'success'}>
                  Done — written {syncAll.data.summary.written}, unchanged {syncAll.data.summary.unchanged}, skipped {syncAll.data.summary.skipped}, errors {syncAll.data.summary.errors}
                </Banner>
              )}
              {syncAll.error && <Banner tone="critical" title="Shopify sync failed">{syncAll.error.message}</Banner>}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* ——— recent imports ——— */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">Recent imports</Text>
                <Button variant="plain" onClick={() => navigate('/imports')}>View all →</Button>
              </InlineStack>
              {!imports.data ? (
                <Text as="p" tone="subdued">Loading…</Text>
              ) : imports.data.data.length === 0 ? (
                <Text as="p" tone="subdued">No imports yet. Pull from a vendor or upload a CSV to get started.</Text>
              ) : (
                <BlockStack gap="200">
                  {imports.data.data.slice(0, 5).map((j) => (
                    <Box
                      key={j.id}
                      padding="300"
                      borderRadius="200"
                      borderWidth="025"
                      borderColor="border"
                      background="bg-surface"
                    >
                      <InlineStack align="space-between" blockAlign="center" gap="200" wrap={false}>
                        <BlockStack gap="050">
                          <InlineStack gap="200" blockAlign="center">
                            <Text as="span" variant="bodyMd" fontWeight="semibold">{j.source_guide}</Text>
                            <Text as="span" variant="bodySm" tone="subdued">·</Text>
                            <Text as="span" variant="bodySm" tone="subdued">
                              {j.filename === 'vendor-pull' ? 'vendor pull' : j.filename}
                            </Text>
                          </InlineStack>
                          <Text as="span" variant="bodySm" tone="subdued">
                            {j.row_count.toLocaleString()} rows · new {j.new_count.toLocaleString()} · upd {j.update_count.toLocaleString()} · bad {j.invalid_count.toLocaleString()} · {ago(j.applied_at ?? j.created_at)}
                          </Text>
                        </BlockStack>
                        <InlineStack gap="200" blockAlign="center">
                          <StatusBadge status={j.status} />
                          <Button variant="plain" onClick={() => navigate(`/imports/${j.id}`)}>Open</Button>
                        </InlineStack>
                      </InlineStack>
                    </Box>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function StatCard({ label, value, hint, onClick }: { label: string; value: string; hint?: string; onClick?: () => void }) {
  return (
    <Box
      padding="400"
      borderRadius="300"
      background="bg-surface"
      borderWidth="025"
      borderColor="border"
      minHeight="100px"
    >
      <BlockStack gap="100">
        <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
        <Text as="p" variant="heading2xl">{value}</Text>
        {hint && <Text as="p" variant="bodySm" tone="subdued">{hint}</Text>}
        {onClick && (
          <Box paddingBlockStart="200">
            <Button variant="plain" onClick={onClick}>Open →</Button>
          </Box>
        )}
      </BlockStack>
    </Box>
  );
}

function VendorCard({ vendor, loading, onClick, count, source }: {
  vendor: string; loading: boolean; onClick: () => void; count: number; source: string;
}) {
  return (
    <Box padding="400" borderRadius="300" borderWidth="025" borderColor="border" background="bg-surface">
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h3" variant="headingMd">{vendor}</Text>
          <Badge tone="info">{`${count.toLocaleString()} wheels`}</Badge>
        </InlineStack>
        <Text as="p" variant="bodySm" tone="subdued">{source}</Text>
        <Box paddingBlockStart="200">
          <Button variant="primary" loading={loading} onClick={onClick}>
            {loading ? 'Queued…' : `Pull fresh from ${vendor}`}
          </Button>
        </Box>
      </BlockStack>
    </Box>
  );
}

function NavTile({ title, desc, onClick }: { title: string; desc: string; onClick: () => void }) {
  return (
    <Box
      padding="400"
      borderRadius="300"
      borderWidth="025"
      borderColor="border"
      background="bg-surface"
    >
      <BlockStack gap="200">
          <div
          role="button"
          tabIndex={0}
          style={{ cursor: 'pointer' }}
          onClick={onClick}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
        >
          <Text as="span" variant="headingSm">{title} →</Text>
        </div>
        <Text as="p" variant="bodySm" tone="subdued">{desc}</Text>
      </BlockStack>
    </Box>
  );
}

function ActionRow({ label, detail, buttonLabel, loading, onClick, variant }: {
  label: string; detail: string; buttonLabel: string; loading: boolean;
  onClick: () => void; variant?: 'primary';
}) {
  return (
    <InlineStack align="space-between" blockAlign="center" gap="400" wrap={false}>
      <BlockStack gap="050">
        <Text as="p" variant="bodyMd" fontWeight="semibold">{label}</Text>
        <Text as="p" variant="bodySm" tone="subdued">{detail}</Text>
      </BlockStack>
      <Button variant={variant} loading={loading} onClick={onClick}>{buttonLabel}</Button>
    </InlineStack>
  );
}

function FeedStatusLine({ label, state, lastPullAt, rowsProcessed, newCount, updateCount }: {
  label: string;
  state: 'connected' | 'error' | 'unknown' | 'stale' | 'missing' | 'not_provided';
  lastPullAt: string | null;
  rowsProcessed: number | null;
  newCount?: number | null;
  updateCount?: number | null;
}) {
  const tone: 'success' | 'critical' | 'attention' | 'warning' | 'info' =
    state === 'connected' ? 'success'
    : state === 'error' ? 'critical'
    : state === 'stale' || state === 'missing' ? 'warning'
    : state === 'not_provided' ? 'info'
    : 'attention';
  const text =
    state === 'connected' ? 'Connected'
    : state === 'error' ? 'Error'
    : state === 'stale' ? 'Stale'
    : state === 'missing' ? 'Missing'
    : state === 'not_provided' ? 'Not provided'
    : 'Unknown';
  return (
    <BlockStack gap="050">
      <InlineStack gap="200" blockAlign="center" wrap={false}>
        <Text as="span" variant="bodySm">{label}</Text>
        <Badge tone={tone}>{text}</Badge>
      </InlineStack>
      <Text as="span" variant="bodySm" tone="subdued">
        {`Last pull: ${lastPullAt ? ago(lastPullAt) : 'never'}`}
        {rowsProcessed != null ? ` · ${rowsProcessed.toLocaleString()} rows` : ''}
        {newCount != null && updateCount != null ? ` · ${newCount} new · ${updateCount} upd` : ''}
      </Text>
    </BlockStack>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone = status === 'applied' ? 'success'
    : status === 'failed' ? 'critical'
    : status === 'processing' ? 'attention'
    : status === 'preview' ? 'info'
    : undefined;
  return <Badge tone={tone}>{status}</Badge>;
}
